import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { bapi } from '@/lib/billingApi';
import { SubscriptionCard } from '@/components/billing/SubscriptionCard';
import { BillingHistory } from '@/components/billing/BillingHistory';
import { AnnualSwitchOffer } from '@/components/billing/AnnualSwitchOffer';
import { OfficialsCardSwitch } from '@/components/billing/OfficialsCardSwitch';
import { TestModeBanner } from '@/components/billing/StatusPill';

export default function BillingPage() {
  const { isAuthenticated, isLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    bapi('/billing/overview').then(setData).catch((e) => toast.error(e.message));
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) { navigate('/login?redirect=%2Fbilling'); return; }
    load();
  }, [isAuthenticated, isLoading, navigate, load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600" data-testid="billing-back-link"><ChevronLeft className="w-5 h-5" />Back</Link>
          <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>BILLING</h1>
          <div className="w-16" />
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 flex flex-col gap-10" data-testid="billing-page">
        {!data ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#00247D]" /></div> : (
          <>
            {data.test_mode && <TestModeBanner />}
            <section>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-amber-600">Your plans</p>
              <h2 className="text-3xl sm:text-4xl font-black uppercase text-slate-900 mt-1" style={{ fontFamily: 'Barlow Condensed' }}>Subscriptions</h2>
              {data.subscriptions.length ? (
                <div className="grid md:grid-cols-2 gap-5 mt-5" data-testid="billing-subscriptions">
                  {data.subscriptions.map((s, i) => (
                    <div key={s.id} className="animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'both' }}>
                      <SubscriptionCard sub={s} onChanged={load} cardEnabled={data.card_enabled} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="billing-no-subscriptions">
                  <p className="text-slate-600">You're on the Free plan.</p>
                  <Link to="/pricing"><Button className="mt-4 bg-[#00247D] hover:bg-[#001A5C]" data-testid="billing-see-plans-btn">See plans</Button></Link>
                </div>
              )}
              {data.subscriptions.filter((s) => s.annual_switch).map((s) => (
                <div key={`offer-${s.id}`} className="mt-5"><AnnualSwitchOffer sub={s} cardEnabled={data.card_enabled} /></div>
              ))}
              {data.subscriptions.filter((s) => s.card_switch).map((s) => (
                <div key={`card-switch-${s.id}`} className="mt-5"><OfficialsCardSwitch sub={s} cardEnabled={data.card_enabled} /></div>
              ))}
            </section>
            <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4"><Receipt className="w-5 h-5 text-[#00247D]" /><h2 className="text-xl font-black uppercase text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>Billing history</h2></div>
              <BillingHistory payments={data.payments} />
            </section>
          </>
        )}
      </main>
    </div>
  );
}
